var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import BigNumber from 'bignumber.js';
import { getIcon } from './getIcon';
import { getSuiClient } from './client';
import { AlphalendClient } from 'alphalend-sdk';
import { marketsArray } from '../data/marketInfo';
import { getMoveErrorMessage } from './errorMaps';
// Constants
const MIN_SUI_BALANCE = 0.1;
const SUI_DECIMALS = 9;
const EXPLORER_BASE_URL = 'https://suiscan.xyz';
// Helper functions
const dispatchTransactionEvent = (txType, success) => {
    const eventType = success ? 'transactionSuccess' : 'transactionError';
    document.dispatchEvent(new CustomEvent(eventType, { detail: { txType, success } }));
};
const getTransactionTitle = (status, action) => {
    if (status === 'awaiting')
        return 'Awaiting Response';
    if (status === 'insufficient_gas_balance')
        return 'Insufficient Gas Balance';
    if (status === 'cancelled')
        return 'Transaction Cancelled';
    const titleMap = {
        Supply: 'Supply Successful',
        Withdraw: 'Withdraw Successful',
        Borrow: 'Borrow Successful',
        Repay: 'Repay Successful',
    };
    if (status === 'success' && action) {
        return titleMap[action];
    }
    if (status === 'error' && action) {
        return `${action} Failed`;
    }
    return 'Transaction Failed';
};
const createExplorerLink = (network, digest) => {
    if (!digest)
        return '';
    const networkSuffix = network === 'testnet' ? 'testnet' : 'mainnet';
    return `${EXPLORER_BASE_URL}/${networkSuffix}/tx/${digest}`;
};
const getTransactionDescription = (status, action, displayAmount, tokenSymbol, errorMessage) => {
    if (status === 'awaiting') {
        return 'Requesting wallet to approve transaction.';
    }
    if (status === 'insufficient_gas_balance') {
        return `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`;
    }
    if (status === 'cancelled') {
        return `${action} transaction was cancelled.`;
    }
    const actionVerb = {
        Supply: 'supplied',
        Withdraw: 'withdrawn',
        Borrow: 'borrowed',
        Repay: 'repaid',
    };
    if (status === 'success' && action && displayAmount && tokenSymbol) {
        return `You have successfully ${actionVerb[action]} ${displayAmount} ${tokenSymbol}.`;
    }
    if (status === 'error' && action) {
        return `Failed to ${action.toLowerCase()} ${displayAmount || ''} ${tokenSymbol || ''}. ${errorMessage || ''}`;
    }
    return errorMessage || 'Transaction failed';
};
const createNotificationInfo = (network, status, params = {}) => {
    const { result, action, token, displayAmount, errorMessage } = params;
    const explorerLink = createExplorerLink(network, result === null || result === void 0 ? void 0 : result.digest);
    const notificationConfigs = {
        awaiting: {
            title: getTransactionTitle('awaiting'),
            description: getTransactionDescription('awaiting'),
            spinner: true,
        },
        success: {
            title: getTransactionTitle('success', action),
            description: getTransactionDescription('success', action, displayAmount, token === null || token === void 0 ? void 0 : token.symbol),
            image: getIcon('success'),
            link: explorerLink,
        },
        error: {
            title: getTransactionTitle('error', action),
            description: getTransactionDescription('error', action, displayAmount, token === null || token === void 0 ? void 0 : token.symbol, errorMessage),
            image: getIcon('failed'),
            link: explorerLink,
        },
        cancelled: {
            title: getTransactionTitle('cancelled', action),
            description: getTransactionDescription('cancelled', action),
            image: getIcon('failed'),
        },
        insufficient_gas_balance: {
            title: getTransactionTitle('insufficient_gas_balance'),
            description: getTransactionDescription('insufficient_gas_balance'),
            image: getIcon('failed'),
        },
    };
    return notificationConfigs[status];
};
export const alphalendTransactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { amount, network, action, modalState, address, positionCapId, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, displayAmount, suiBalance, resetInputValues, notifyTransaction, suppliedAndBorrowedCoinTypes = [], } = params;
    // Helper function to show notification and open modal
    const showNotification = (status, notifParams = {}) => {
        setNotificationInfo(createNotificationInfo(network, status, notifParams));
        setIsNotificationOpen(true);
    };
    // Helper function to handle error state
    const handleError = (errorMessage, shouldResetInputs = true, result) => {
        // Convert raw error message to user-friendly message
        const contractErrorMessage = getMoveErrorMessage(errorMessage);
        showNotification('error', {
            errorMessage: contractErrorMessage,
            action,
            token: modalState.token,
            displayAmount,
            result,
        });
        setIsTransactionLoading(false);
        if (shouldResetInputs)
            resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
        dispatchTransactionEvent(action.toLowerCase(), false);
    };
    // Early validation
    if (!address || !modalState.token) {
        console.error('Address and token are required for alphalend transactions');
        return;
    }
    // Validate action requires positionCapId (except for Supply)
    if (action !== 'Supply' && !positionCapId) {
        console.error('Position Cap ID is required for', action);
        showNotification('error', {
            action,
            token: modalState.token,
            displayAmount,
            errorMessage: 'Position Cap ID is required. Please connect your wallet.',
        });
        return;
    }
    // Convert raw SUI balance to decimal format
    const suiBalanceDecimal = new BigNumber(suiBalance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS));
    if (suiBalanceDecimal.lt(MIN_SUI_BALANCE)) {
        showNotification('insufficient_gas_balance');
        return;
    }
    const suiClient = getSuiClient(network || 'mainnet');
    const alphalendClient = new AlphalendClient(network || 'mainnet', suiClient);
    try {
        const marketId = modalState.token.marketId;
        const marketInfo = marketsArray.find((m) => m.id === marketId);
        if (!marketInfo) {
            throw new Error(`Market info not found for market ID: ${marketId}`);
        }
        const coinType = marketInfo.coinType;
        const expo = marketInfo.expo;
        // Convert amount to big number with proper decimals
        // Special case: max withdrawal amount is represented as u64::MAX
        const amountBigNumber = amount === '18446744073709551615'
            ? new BigNumber(amount)
            : new BigNumber(amount).multipliedBy(new BigNumber(10).pow(expo));
        const amountBigInt = BigInt(amountBigNumber.toFixed(0));
        // Build transaction parameters
        const txParams = {
            marketId,
            amount: amountBigInt,
            coinType,
            positionCapId: positionCapId || undefined,
            address,
        };
        // Show awaiting notification
        showNotification('awaiting');
        setIsTransactionLoading(true);
        let transactionBlock;
        // Handle different action types
        switch (action) {
            case 'Supply':
                transactionBlock = yield alphalendClient.supply(txParams);
                break;
            case 'Withdraw':
                transactionBlock = yield alphalendClient.withdraw(Object.assign(Object.assign({}, txParams), { positionCapId: positionCapId, priceUpdateCoinTypes: suppliedAndBorrowedCoinTypes.length > 0 ? suppliedAndBorrowedCoinTypes : [coinType] }));
                break;
            case 'Borrow':
                transactionBlock = yield alphalendClient.borrow(Object.assign(Object.assign({}, txParams), { positionCapId: positionCapId, priceUpdateCoinTypes: suppliedAndBorrowedCoinTypes.length > 0 ? suppliedAndBorrowedCoinTypes : [coinType] }));
                break;
            case 'Repay':
                transactionBlock = yield alphalendClient.repay(Object.assign(Object.assign({}, txParams), { positionCapId: positionCapId }));
                break;
            default:
                throw new Error(`Unsupported action: ${action}`);
        }
        if (!transactionBlock) {
            throw new Error('Failed to create transaction block');
        }
        signAndExecuteTransaction({ transaction: transactionBlock }, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                var _a, _b, _c;
                try {
                    const txbCheck = yield suiClient.waitForTransaction({
                        digest: result.digest,
                        options: { showEffects: true },
                    });
                    if (((_b = (_a = txbCheck.effects) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.status) === 'success') {
                        showNotification('success', {
                            result,
                            action,
                            token: modalState.token,
                            displayAmount,
                        });
                        resetInputValues === null || resetInputValues === void 0 ? void 0 : resetInputValues();
                        // Notify websocket service about transaction
                        // For Supply, positionCapId might be null for first-time supply
                        // The backend should handle this case
                        if (notifyTransaction) {
                            notifyTransaction(marketId, positionCapId, action.toLowerCase());
                        }
                        dispatchTransactionEvent(action.toLowerCase(), true);
                    }
                    else {
                        const errorMessage = ((_c = txbCheck.effects) === null || _c === void 0 ? void 0 : _c.status.error) || 'Transaction failed';
                        console.error(`Error in ${action}:`, errorMessage);
                        handleError(errorMessage, true, result);
                    }
                }
                catch (error) {
                    console.error('Error waiting for transaction:', error);
                    handleError('Failed to confirm transaction', false, result);
                }
                finally {
                    setIsTransactionLoading(false);
                }
            }),
            onError: (error) => {
                console.error(`Error in ${action}:`, error);
                const message = error.message || 'Transaction cancelled';
                handleError(message);
            },
        });
    }
    catch (error) {
        const err = error;
        console.error(`Error in ${action}:`, err);
        handleError(err.message);
    }
});
// Claim Rewards Transaction Handler (separate from main transaction handler)
export const claimRewardsTransactionHandler = (params) => __awaiter(void 0, void 0, void 0, function* () {
    const { address, positionCapId, network, notifyTransaction, signAndExecuteTransaction, setNotificationInfo, setIsNotificationOpen, setIsTransactionLoading, claimAll, claimAlpha, suiBalance, resetInputValues, } = params;
    const suiClient = getSuiClient(network || 'mainnet');
    const alphalendClient = new AlphalendClient(network || 'mainnet', suiClient);
    // Helper to show notification
    const showNotification = (status, errorMessage, result) => {
        const explorerLink = createExplorerLink(network, result === null || result === void 0 ? void 0 : result.digest);
        // Convert error message to user-friendly format if provided
        const friendlyErrorMessage = errorMessage
            ? getMoveErrorMessage(errorMessage)
            : 'Failed to claim rewards.';
        const notifications = {
            awaiting: {
                title: 'Awaiting Response',
                description: 'Requesting wallet to approve transaction.',
                spinner: true,
            },
            success: {
                title: 'Claim Rewards Successful',
                description: 'You have successfully claimed your rewards.',
                image: getIcon('success'),
                link: explorerLink,
            },
            error: {
                title: 'Claim Rewards Failed',
                description: friendlyErrorMessage,
                image: getIcon('failed'),
                link: explorerLink,
            },
            cancelled: {
                title: 'Transaction Cancelled',
                description: 'Claim rewards transaction was cancelled.',
                image: getIcon('failed'),
            },
            insufficient_gas_balance: {
                title: 'Insufficient Gas Balance',
                description: `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`,
                image: getIcon('failed'),
            },
        };
        setNotificationInfo(notifications[status]);
        setIsNotificationOpen(true);
    };
    if (!address || !positionCapId) {
        console.error('Address and Position Cap ID are required for claiming rewards');
        return;
    }
    try {
        // Check SUI balance
        const suiBalanceDecimal = new BigNumber(suiBalance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS));
        if (suiBalanceDecimal.lte(MIN_SUI_BALANCE)) {
            showNotification('insufficient_gas_balance');
            return;
        }
        // Show awaiting notification
        showNotification('awaiting');
        setIsTransactionLoading(true);
        // Call SDK to claim rewards
        const transactionBlock = yield alphalendClient.claimRewards({
            positionCapId,
            address,
            claimAll,
            claimAlpha,
        });
        if (!transactionBlock) {
            throw new Error('Failed to create transaction block');
        }
        signAndExecuteTransaction({ transaction: transactionBlock }, {
            onSuccess: (result) => __awaiter(void 0, void 0, void 0, function* () {
                var _a, _b, _c;
                try {
                    const txbCheck = yield suiClient.waitForTransaction({
                        digest: result.digest,
                        options: { showEffects: true },
                    });
                    if (((_b = (_a = txbCheck.effects) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.status) === 'success') {
                        showNotification('success', undefined, result);
                        setIsTransactionLoading(false);
                        // Notify websocket service about transaction
                        if (notifyTransaction && positionCapId) {
                            console.log('💰 [CLAIM_REWARD] Sending notification to WebSocket server for position:', positionCapId);
                            notifyTransaction(positionCapId);
                        }
                        // Dispatch transaction completion event
                        dispatchTransactionEvent('claim_reward', true);
                        // Reset input values if provided
                        if (resetInputValues) {
                            resetInputValues();
                        }
                    }
                    else {
                        const errorMessage = ((_c = txbCheck.effects) === null || _c === void 0 ? void 0 : _c.status.error) || 'Unexpected error occurred.';
                        console.error('Error in Claim Rewards:', errorMessage);
                        showNotification('error', errorMessage, result);
                        setIsTransactionLoading(false);
                        if (resetInputValues) {
                            resetInputValues();
                        }
                        dispatchTransactionEvent('claim_reward', false);
                    }
                }
                catch (error) {
                    console.error('Error waiting for transaction:', error);
                    showNotification('error', 'Failed to confirm transaction', result);
                    setIsTransactionLoading(false);
                }
            }),
            onError: (error) => {
                console.error('Error in Claim Rewards:', error);
                const rawMessage = error.message || 'You cancelled the claim rewards.';
                // Convert raw error message to user-friendly message
                const friendlyMessage = getMoveErrorMessage(rawMessage);
                showNotification('error', friendlyMessage);
                setIsTransactionLoading(false);
                if (resetInputValues) {
                    resetInputValues();
                }
                dispatchTransactionEvent('claim_reward', false);
            },
        });
    }
    catch (error) {
        const err = error;
        console.log('Error in Claim Rewards:', err);
        const rawMessage = err.message || 'Unexpected error occurred.';
        // Convert raw error message to user-friendly message
        const friendlyMessage = getMoveErrorMessage(rawMessage);
        showNotification('error', friendlyMessage);
        setIsTransactionLoading(false);
        if (resetInputValues) {
            resetInputValues();
        }
        dispatchTransactionEvent('claim_reward', false);
    }
});
